import { useEffect, useState } from 'react';
import { Modal, Form, Select, DatePicker, InputNumber, Button, Table, Row, Col, Space, Tag, Input, message } from '../../ui';
import { Trash2, Plus } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { warehouseLabel } from '../../lib/warehouseLabel';
import { fmtQty } from '../../lib/format';
import { displayBoxLoose } from '../../lib/packaging';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Line {
  productId: string;
  sku: string;
  name: string;
  qty: number;
  unitsPerBox?: number; // for box/loose display of a packed line (qty stays total base units)
}

export function TransferFormModal({ open, onClose }: Props) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const [lines, setLines] = useState<Line[]>([]);
  const [productId, setProductId] = useState<string | undefined>();
  const [qty, setQty] = useState<number>(1);
  const [box, setBox] = useState<number>(0);
  const [loose, setLoose] = useState<number>(0);

  const { data: warehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => request<any[]>({ url: '/warehouses' }),
    enabled: open,
  });
  // Catalog with live per-warehouse stock — the transfer source list is built from what's actually in
  // the chosen "From" warehouse, so you can't pick (or transfer) stock that isn't there.
  const { data: catalog } = useQuery({
    queryKey: ['catalog-with-stock-transfer'],
    queryFn: () => request<any[]>({ url: '/catalog/with-stock' }),
    enabled: open,
  });

  // Watch both sides so each dropdown can grey out the warehouse already chosen on the other —
  // a transfer's source and destination must differ. The master warehouse is badged in the list.
  const fromId = Form.useWatch('fromWarehouseId', form);
  const toId = Form.useWatch('toWarehouseId', form);
  const whOptions = (disabledId?: string) =>
    (warehouses ?? []).map((w: any) => ({
      value: w.id,
      disabled: disabledId != null && String(w.id) === String(disabledId),
      label: warehouseLabel(w),
    }));

  // Source availability is warehouse-specific — reset the picked lines whenever "From" changes.
  useEffect(() => { setLines([]); setProductId(undefined); setQty(1); setBox(0); setLoose(0); }, [fromId]);
  // Reset the entry quantities when a different product is picked.
  useEffect(() => { setQty(1); setBox(0); setLoose(0); }, [productId]);

  // Products actually in stock in the chosen source warehouse, with the available qty there.
  const fromCode = (warehouses ?? []).find((w: any) => String(w.id) === String(fromId))?.code as string | undefined;
  const inStock = (catalog ?? [])
    .map((p: any) => ({ productId: String(p.productId), sku: p.sku, name: p.name, avail: Number(p.stock?.warehouses?.[fromCode ?? ''] ?? 0), boxed: !!p.boxPackingEnabled && Number(p.unitsPerBox) > 0, upb: Number(p.unitsPerBox) || 0 }))
    .filter((p) => p.avail > 0);
  const selProd = inStock.find((p) => String(p.productId) === String(productId));
  const inLine = lines.find((l) => String(l.productId) === String(productId))?.qty ?? 0;
  const maxAddable = selProd ? Math.max(0, selProd.avail - inLine) : 0;
  // The quantity being added: box × upb + loose for a packed variant, else the plain qty.
  const entryQty = selProd?.boxed ? box * selProd.upb + loose : qty;

  const addLine = () => {
    if (!fromId) { message.warning('Pick the source warehouse first'); return; }
    const p = selProd;
    if (!p || entryQty <= 0) { message.warning('Pick product and qty'); return; }
    if (entryQty > maxAddable + 1e-6) {
      message.warning(`Only ${maxAddable} of ${p.name} available in this warehouse${inLine ? ` (${inLine} already added)` : ''}`);
      return;
    }
    // Merge into an existing line for the same SKU instead of creating duplicates.
    setLines((ls) => {
      const i = ls.findIndex((l) => String(l.productId) === String(p.productId));
      if (i >= 0) {
        const next = [...ls];
        next[i] = { ...next[i], qty: next[i].qty + entryQty };
        return next;
      }
      return [...ls, { productId: p.productId, sku: p.sku, name: p.name, qty: entryQty, unitsPerBox: p.boxed ? p.upb : undefined }];
    });
    setProductId(undefined); setQty(1); setBox(0); setLoose(0);
  };

  const save = useMutation({
    mutationFn: (vals: any) => request<any>({
      url: '/stock-transfers', method: 'POST',
      data: {
        fromWarehouseId: vals.fromWarehouseId,
        toWarehouseId: vals.toWarehouseId,
        transferDate: vals.transferDate.format('YYYY-MM-DD'),
        remarks: vals.remarks,
        lines: lines.map((l) => ({ productId: l.productId, qty: l.qty })),
      },
    }),
    onSuccess: (r) => {
      // Standalone transfers are created REQUESTED (await dispatch); consolidation transfers come back IN_TRANSIT.
      message.success(`Transfer ${r.transferNo} created · ${r.status === 'IN_TRANSIT' ? 'in transit' : 'awaiting dispatch'}`);
      qc.invalidateQueries({ queryKey: ['stock-transfers'] });
      setLines([]);
      form.resetFields();
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      width={780}
      title="New Stock Transfer"
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() => {
              if (lines.length === 0) { message.warning('Add at least one line'); return; }
              form.submit();
            }}
          >
            Create Transfer
          </Button>
        </>
      }
    >
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name="transferDate" label="Date" rules={[{ required: true }]} initialValue={dayjs()}>
              <DatePicker className="w-full" />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item name="fromWarehouseId" label="From" rules={[{ required: true }]}>
              <Select placeholder="Source warehouse" options={whOptions(toId)} />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item
              name="toWarehouseId"
              label="To"
              dependencies={['fromWarehouseId']}
              rules={[
                { required: true },
                ({ getFieldValue }: any) => ({
                  validator(_: any, v: any) {
                    return v && v === getFieldValue('fromWarehouseId')
                      ? Promise.reject(new Error('Destination must differ from source'))
                      : Promise.resolve();
                  },
                }),
              ]}
            >
              <Select placeholder="Destination warehouse" options={whOptions(fromId)} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={8} style={{ marginBottom: 12 }} align="middle">
          <Col span={14}>
            <Select
              showSearch
              optionFilterProp="searchLabel"
              placeholder={fromId ? 'Pick a product in stock here' : 'Pick the source warehouse first'}
              disabled={!fromId}
              value={productId}
              onChange={setProductId}
              className="w-full"
              notFoundContent={fromId ? 'No stock in this warehouse' : 'Select a source warehouse'}
              options={inStock.map((p) => ({
                value: p.productId,
                searchLabel: `${p.sku} · ${p.name}`,
                label: (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.sku} · {p.name}</span>
                    <Tag tone="blue" style={{ margin: 0, flexShrink: 0 }}>{p.avail}</Tag>
                  </span>
                ),
              }))}
            />
          </Col>
          <Col span={6}>
            {selProd?.boxed ? (
              <Space size={4}>
                <InputNumber placeholder="Box" value={box} onChange={(v) => setBox(Math.max(0, Math.floor(Number(v ?? 0))))} min={0} precision={0} disabled={!productId} style={{ width: 60 }} />
                <span style={{ fontSize: 11, color: '#999' }}>box</span>
                <InputNumber placeholder="Loose" value={loose} onChange={(v) => setLoose(Math.max(0, Number(v ?? 0)))} min={0} disabled={!productId} style={{ width: 60 }} />
              </Space>
            ) : (
              <InputNumber placeholder="Qty" value={qty} onChange={(v) => setQty(Number(v ?? 0))} min={0.001}
                max={selProd ? maxAddable : undefined} disabled={!productId} style={{ width: '100%' }} />
            )}
          </Col>
          <Col span={4}>
            <Button variant="primary" icon={<Plus size={16} />} onClick={addLine} block
              disabled={!selProd || entryQty <= 0 || entryQty > maxAddable + 1e-6}>Add</Button>
          </Col>
        </Row>
        {selProd && (
          <div style={{ marginTop: -6, marginBottom: 10, fontSize: 12, color: entryQty > maxAddable + 1e-6 ? '#cf1322' : '#8c8c8c' }}>
            Available in this warehouse: <b>{fmtQty(selProd.avail)}</b>
            {selProd.boxed ? ` (${displayBoxLoose(selProd.avail, selProd.upb)} · ${selProd.upb}/box)` : ''}
            {selProd.boxed && entryQty > 0 ? ` · adding ${fmtQty(entryQty)}` : ''}
            {inLine ? ` · ${fmtQty(inLine)} already added` : ''}
            {entryQty > maxAddable + 1e-6 ? ` — you can add at most ${fmtQty(maxAddable)}` : ''}
          </div>
        )}

        <Table
          rowKey={(_, i) => String(i)}
          dataSource={lines}
          pagination={false}
          size="small"
          columns={[
            { title: 'SKU', dataIndex: 'sku' },
            { title: 'Product', dataIndex: 'name' },
            {
              title: 'Qty', dataIndex: 'qty', align: 'right' as const,
              render: (v: number, r: Line) => (
                <div>
                  <Tag tone="blue" style={{ margin: 0 }}>{fmtQty(v)}</Tag>
                  {r.unitsPerBox ? <div style={{ fontSize: 11, color: '#8c8c8c' }}>{displayBoxLoose(v, r.unitsPerBox)}</div> : null}
                </div>
              ),
            },
            {
              title: '',
              render: (_, __, i) => (
                <Button variant="soft-danger" aria-label="Remove line" icon={<Trash2 size={16} />}
                  onClick={() => setLines(lines.filter((_, idx) => idx !== i))} />
              ),
            },
          ]}
        />

        <Form.Item name="remarks" label="Remarks" style={{ marginTop: 12 }}>
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
